import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, ChevronDown, Check } from "lucide-react";
import { cn } from "../ui";

/**
 * CourseSwitcher — picks which enrollment the student is giving feedback for.
 *
 * A listbox rather than a `NativeSelect` because each option is two lines plus a
 * "already submitted" mark, which a native `<option>` cannot render.
 *
 * ── Keyboard ─────────────────────────────────────────────────────────────────
 * The options are `<button role="option">`, not `<li onClick>`. As `<li>`s they
 * were unreachable by keyboard entirely — the listbox could be opened and then
 * nothing in it could be selected. Escape closes and returns focus to the
 * trigger.
 *
 * Props:
 *   enrollments — [{ batchId, courseName, batchName, feedback }]
 *   selectedIdx — index of the current enrollment
 *   onSelect    — (idx) => void
 */
const CourseSwitcher = ({ enrollments, selectedIdx, onSelect }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const selected = enrollments[selectedIdx];

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const choose = (idx) => {
    onSelect(idx);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-2.5">
      <span className="tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-content-subtle">
        Course
      </span>

      <div className="tw-relative tw-min-w-0 tw-flex-1">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded tw-border tw-border-line tw-bg-surface-raised tw-px-3 tw-py-2 tw-text-left tw-transition-[border-color] hover:tw-border-primary tw-focus-ring"
        >
          <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
            <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-content">
              {selected?.courseName || "Course"}
            </span>
            {selected?.batchName && (
              <span className="tw-text-xs tw-text-content-subtle">{selected.batchName}</span>
            )}
          </span>
          {selected?.feedback && (
            <CheckCircle size={15} className="tw-shrink-0 tw-text-success-fg" />
          )}
          <ChevronDown
            size={16}
            className={cn(
              "tw-shrink-0 tw-text-content-muted tw-transition-transform",
              open && "tw-rotate-180"
            )}
          />
        </button>

        {open && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-z-20"
              aria-hidden="true"
              onClick={() => setOpen(false)}
            />
            <ul
              role="listbox"
              aria-label="Select course"
              className="tw-absolute tw-left-0 tw-right-0 tw-top-[calc(100%+0.375rem)] tw-z-[21] tw-m-0 tw-max-h-80 tw-list-none tw-overflow-y-auto tw-rounded tw-border tw-border-line tw-bg-surface-raised tw-p-1.5 tw-shadow-popover"
            >
              {enrollments.map((enr, i) => (
                <li key={enr.batchId}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === selectedIdx}
                    onClick={() => choose(i)}
                    className={cn(
                      "tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-sm tw-border-0 tw-px-2.5 tw-py-2 tw-text-left tw-transition-[background-color]",
                      "tw-focus-ring-inset",
                      i === selectedIdx
                        ? "tw-bg-primary/10"
                        : "tw-bg-transparent hover:tw-bg-surface-sunken"
                    )}
                  >
                    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                      <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
                        {enr.courseName || "Course"}
                      </span>
                      {enr.batchName && (
                        <span className="tw-text-[0.6875rem] tw-text-content-subtle">
                          {enr.batchName}
                        </span>
                      )}
                    </span>
                    {enr.feedback ? (
                      <CheckCircle
                        size={14}
                        className="tw-shrink-0 tw-text-success-fg"
                        aria-label="Feedback submitted"
                      />
                    ) : (
                      i === selectedIdx && (
                        <Check size={16} className="tw-shrink-0 tw-text-primary" aria-hidden="true" />
                      )
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default CourseSwitcher;
